import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { client } from '@/client';
import { Course } from '@/client/courses';
import { SystemRoles } from '@/client/users';
import { TranslationFunctionType } from '@/common/types';
import { CourseProcessing } from '@/components/courses/CourseProcessing';
import { FormikDropdown, FormikMultiSelect } from '@/components/form';
import { useNotifications } from '@/hooks/notifications.hook';
import { useCourse } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton, FlexContainer } from '@/ui';
import {
  courseLanguagesOptions,
  getTargetLanguagesRequiringEnglish,
  getVideoFallbackLanguages,
  hasTranslationAccess,
  sortAlphabetically,
} from '@/utils/helpers';
import { Field, Form, Formik, FormikProps } from 'formik';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { MultiSelectChangeEvent } from 'primereact/multiselect';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import * as Yup from 'yup';
import { VideoFallbackMessage } from './VideoFallbackMessage';

const Title = styled.h2`
  font-family: var(--font-family);
  font-weight: 700;
  font-size: 24px;
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  white-space: pre-wrap;
`;

const Description = styled.p`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 16px;
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  white-space: pre-wrap;
`;

export const STORAGE_KEY_TRANSLATION_IN_PROGRESS = (courseId: string) =>
  `translation-in-progress-${courseId}`;
export const TRANSLATION_STORAGE_KEY = (courseId: string) =>
  `translation-banner-show-${courseId}`;
export const TRANSLATION_FAILED_LANGUAGES_KEY = (courseId: string) =>
  `translation-failed-languages-${courseId}`;
export const TRANSLATION_SOURCE_LANGUAGE_KEY = (courseId: string) =>
  `translation-source-language-${courseId}`;

export function clearCourseTranslationSessionStorage(courseId: string): void {
  sessionStorage.removeItem(TRANSLATION_STORAGE_KEY(courseId));
  sessionStorage.removeItem(STORAGE_KEY_TRANSLATION_IN_PROGRESS(courseId));
  sessionStorage.removeItem(TRANSLATION_FAILED_LANGUAGES_KEY(courseId));
  sessionStorage.removeItem(TRANSLATION_SOURCE_LANGUAGE_KEY(courseId));
}

type TranslationFormValues = {
  sourceLanguage?: LanguagesEnum;
  targetLanguages: LanguagesEnum[];
};

const createValidationSchema = (
  showSourceLanguage: boolean,
  t: TranslationFunctionType,
) =>
  Yup.object().shape({
    sourceLanguage: showSourceLanguage
      ? Yup.string()
          .oneOf(Object.values(LanguagesEnum))
          .required(t('course.translation.validation.sourceLanguageRequired'))
      : Yup.string().optional(),
    targetLanguages: Yup.array()
      .of(Yup.string().oneOf(Object.values(LanguagesEnum)))
      .min(1, t('course.translation.validation.targetLanguagesMin'))
      .required(t('course.translation.validation.targetLanguagesRequired')),
    translateMetadata: Yup.boolean(),
    translateQuizzes: Yup.boolean(),
    translatePresentations: Yup.boolean(),
    translateResources: Yup.boolean(),
  });

const createInitialValues = (
  course: Course,
  showSourceLanguage: boolean,
): TranslationFormValues => {
  const baseValues: TranslationFormValues = {
    targetLanguages: [],
  };

  if (showSourceLanguage) {
    baseValues.sourceLanguage =
      course.courseLanguages.length > 0
        ? course.courseLanguages[0]
        : LanguagesEnum.EN;
  }

  return baseValues;
};

export type CourseTranslationProps = {
  course: Course;
  onCancel: () => void;
  onComplete?: () => void;
  showSourceLanguageSelector?: boolean;
  onTranslationFinished?: () => void;
};

export const CourseTranslation: React.FC<CourseTranslationProps> = ({
  course,
  onCancel,
  onComplete,
  showSourceLanguageSelector = false,
  onTranslationFinished,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const { can } = usePermission();
  const handledEventIdRef = useRef<string | null>(null);

  const originalContentId = course.customizationMetadata?.originalContentId;
  const { course: originalCourse } = useCourse({
    courseId: originalContentId,
    enabled: !!originalContentId,
  });

  // SSE notifications for translation
  const { lastMessage, setNotificationParam } = useNotifications(
    client.translations.translationsNotifyUrl,
  );

  // Handle translation SSE notifications
  useEffect(() => {
    if (!lastMessage?.data) return;

    const { event, error } = lastMessage.data;
    const eventId = lastMessage.lastEventId;

    // Prevent duplicate handling of the same event
    // Only dedupe when eventId is a non-empty unique value
    if (
      eventId &&
      eventId.trim() !== '' &&
      handledEventIdRef.current === eventId
    ) {
      return;
    }

    if (event === 'course-translation.finished') {
      // Only track eventId if it's a valid non-empty value
      if (eventId && eventId.trim() !== '') {
        handledEventIdRef.current = eventId;
      }
      setIsTranslating(false);
      setTranslationError(null);

      // Clear in-progress flag and set completed flag
      sessionStorage.removeItem(STORAGE_KEY_TRANSLATION_IN_PROGRESS(course.id));
      sessionStorage.setItem(TRANSLATION_STORAGE_KEY(course.id), 'true');

      const {
        courseTranslation,
        quizTranslations,
        presentationTranslations,
        resourceTranslations,
      } = lastMessage.data.result;

      const failed = [
        ...(courseTranslation?.failedLanguages ?? []),
        ...(quizTranslations ?? []).flatMap(
          ({ failedLanguages }: { failedLanguages: string[] }) =>
            failedLanguages,
        ),
        ...(presentationTranslations ?? []).flatMap(
          ({ failedLanguages }: { failedLanguages: string[] }) =>
            failedLanguages,
        ),
        ...(resourceTranslations ?? []).flatMap(
          ({ failedLanguages }: { failedLanguages: string[] }) =>
            failedLanguages,
        ),
      ];

      const dedupedFailed = [...new Set(failed)];

      if (dedupedFailed.length > 0) {
        sessionStorage.setItem(
          TRANSLATION_FAILED_LANGUAGES_KEY(course.id),
          JSON.stringify(dedupedFailed),
        );
      } else {
        sessionStorage.removeItem(TRANSLATION_FAILED_LANGUAGES_KEY(course.id));
      }

      queryClient.invalidateQueries(['course', course.id]);

      if (onTranslationFinished) {
        onTranslationFinished();
      } else {
        onComplete?.();
      }
    } else if (event === 'course-translation.failed') {
      // Only track eventId if it's a valid non-empty value
      if (eventId && eventId.trim() !== '') {
        handledEventIdRef.current = eventId;
      }
      setIsTranslating(false);
      setTranslationError(error || t('translations.error.generic'));

      // Clear in-progress flag on failure
      sessionStorage.removeItem(STORAGE_KEY_TRANSLATION_IN_PROGRESS(course.id));

      toast?.error(
        t('translations.error.title'),
        error || t('translations.error.generic'),
      );
    }
  }, [lastMessage, course.id, toast, t, onComplete, onTranslationFinished]);

  const handleTranslate = async (values: TranslationFormValues) => {
    if (!values.targetLanguages.length || !currentAccount) {
      toast?.error(
        t('translations.error.title'),
        t('translations.error.selectLanguages'),
      );
      return;
    }

    setIsLoading(true);

    try {
      // Use selected source language if available, otherwise use the first available language.
      const sourceLanguage = values.sourceLanguage || course.courseLanguages[0];

      if (!sourceLanguage) {
        toast?.error(
          t('translations.error.title'),
          t('translations.error.requiredSourceLanguage'),
        );
        return;
      }

      await client.translations.translateCourse(course.id, {
        accountId: currentAccount.id,
        sourceLanguage,
        targetLanguages: values.targetLanguages,
        translateQuizzes: true,
        translatePresentations: true,
        translateResources: true,
      });

      setIsLoading(false);
      setIsTranslating(true);
      setTranslationError(null);

      // Set flag to indicate translation is in progress
      // This allows CourseTranslationStatusBanner to subscribe even if user navigates away
      sessionStorage.setItem(
        STORAGE_KEY_TRANSLATION_IN_PROGRESS(course.id),
        'true',
      );
      sessionStorage.setItem(
        TRANSLATION_SOURCE_LANGUAGE_KEY(course.id),
        sourceLanguage,
      );

      // Connect to SSE to listen for translation events
      setNotificationParam(course.id);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message?.includes(
        'already in progress',
      )
        ? t('translations.error.alreadyInProgress')
        : t('translations.error.generic');

      // Clear in-progress flag on error
      sessionStorage.removeItem(STORAGE_KEY_TRANSLATION_IN_PROGRESS(course.id));

      toast?.error(t('translations.error.title'), errorMessage);
      setIsLoading(false);
      setIsTranslating(false);
      setTranslationError(errorMessage);
    }
  };

  // Filter out Greenlandic (not supported by DeepL)
  const allAvailableLanguages = courseLanguagesOptions
    .filter((lang) => lang.value !== LanguagesEnum.KL)
    .sort((a, b) => sortAlphabetically(a.name, b.name))
    .map((lang) => ({
      label: lang.name,
      value: lang.value,
    }));

  // For source language selector, show all available languages from the course
  const availableSourceLanguages = allAvailableLanguages.filter((lang) =>
    course.courseLanguages.includes(lang.value),
  );

  // For target languages, show all available languages (excluding source if selector is shown)
  const getAvailableTargetLanguages = (selectedSource?: LanguagesEnum) => {
    if (!showSourceLanguageSelector || !selectedSource) {
      return allAvailableLanguages;
    }
    return allAvailableLanguages.filter(
      (lang) => lang.value !== selectedSource,
    );
  };

  // Show processing step when translation is in progress
  if (isTranslating) {
    return (
      <CourseProcessing
        title={t('translations.processing.title')}
        description={t('translations.processing.description')}
        onContinue={onComplete || onCancel}
      />
    );
  }

  const canTranslate = hasTranslationAccess(
    currentAccount?.id || '',
    currentUser?.role.code as SystemRoles,
    course,
    can,
    currentAccount?.isSystem,
  );

  // Show error state if translation failed
  if (!canTranslate || translationError) {
    return (
      <FlexContainer
        direction="column"
        gap={52}
        align="center"
        className="pt-9"
      >
        <FlexContainer direction="column" gap={10} align="center">
          <Title>{t('translations.error.title')}</Title>
          <Description>
            {translationError || t('translations.error.noPermission')}
          </Description>
        </FlexContainer>
        <FlexContainer gap={14} align="center">
          <AppButton
            label={t('button.close')}
            onClick={onCancel}
            type="outlined"
          />
          {canTranslate && (
            <AppButton
              onClick={() => {
                setTranslationError(null);
                setIsTranslating(false);
              }}
            >
              {t('button.tryAgain')}
            </AppButton>
          )}
        </FlexContainer>
      </FlexContainer>
    );
  }

  // Show translation form
  return (
    <FlexContainer direction="column" gap={52} align="center" className="pt-9">
      <FlexContainer direction="column" gap={10} align="center">
        <Title>{t('course.translation.title')}</Title>
        <Description>{t('course.translation.description')}</Description>
      </FlexContainer>

      <Formik
        initialValues={createInitialValues(course, showSourceLanguageSelector)}
        validationSchema={createValidationSchema(showSourceLanguageSelector, t)}
        onSubmit={handleTranslate}
      >
        {({
          values,
          setFieldValue,
          isValid,
        }: FormikProps<TranslationFormValues>) => {
          const availableTargetLanguages = getAvailableTargetLanguages(
            values.sourceLanguage,
          );

          const englishIsBeingAdded = values.targetLanguages.includes(
            LanguagesEnum.EN,
          );

          const allFallbackLanguages = getVideoFallbackLanguages(
            englishIsBeingAdded && originalCourse
              ? originalCourse.courseContents
              : course.courseContents,
            values.targetLanguages,
          );

          const fallbackLanguages = originalCourse
            ? allFallbackLanguages.filter(
                (lang) =>
                  getVideoFallbackLanguages(originalCourse.courseContents, [
                    lang,
                  ]).length > 0,
              )
            : allFallbackLanguages;

          const languagesRequiringEnglish = getTargetLanguagesRequiringEnglish(
            course.courseContents,
            originalCourse?.courseContents,
            values.targetLanguages,
          );

          const languagesRequiringEnglishNames = languagesRequiringEnglish
            .map((lang) => languageDisplayLabel[lang])
            .join(', ');

          const isWaitingForOriginalCourse =
            !!originalContentId && !originalCourse;

          return (
            <Form>
              <FlexContainer direction="column" gap={52}>
                <FlexContainer direction="column" gap={37}>
                  <FlexContainer
                    direction="column"
                    gap={16}
                    align="flex-start"
                    style={{ width: '500px', maxWidth: '100%' }}
                  >
                    {showSourceLanguageSelector && (
                      <div className="w-full">
                        <Field
                          disabled={isLoading}
                          id="sourceLanguage"
                          name="sourceLanguage"
                          filter
                          label={t('translations.translateFrom')}
                          className="w-full white"
                          onChange={(e: DropdownChangeEvent) => {
                            setFieldValue('sourceLanguage', e.value);
                          }}
                          component={FormikDropdown}
                          options={availableSourceLanguages}
                          optionLabel="label"
                          optionValue="value"
                          placeholder={t('translations.selectSource')}
                        />
                      </div>
                    )}

                    <div className="mt-2 w-full">
                      <Field
                        disabled={isLoading}
                        id="targetLanguages"
                        name="targetLanguages"
                        filter
                        label={t('translations.translateTo')}
                        className="w-full bg-white"
                        display="chip"
                        placeholder={t('translations.selectTargets')}
                        onChange={(e: MultiSelectChangeEvent) =>
                          setFieldValue('targetLanguages', e.value)
                        }
                        component={FormikMultiSelect}
                        options={availableTargetLanguages}
                        itemTemplate={(option: any) => (
                          <span>{option.label}</span>
                        )}
                      />
                    </div>
                    {fallbackLanguages.length > 0 && (
                      <VideoFallbackMessage
                        severity="info"
                        text={t('course.video.fallback.translation.v2')}
                        className="w-full"
                      />
                    )}
                    {languagesRequiringEnglish.length > 0 && (
                      <Message
                        severity="warn"
                        text={t('course.video.fallback.englishRequired', {
                          languages: languagesRequiringEnglishNames,
                        })}
                        className="w-full"
                      />
                    )}
                  </FlexContainer>
                </FlexContainer>

                <FlexContainer gap={14} align="center">
                  <AppButton
                    label={t('button.cancel')}
                    onClick={onCancel}
                    isDisabled={isLoading}
                    type="outlined"
                  />
                  <AppButton
                    onClick={() => handleTranslate(values)}
                    isDisabled={
                      isLoading ||
                      !isValid ||
                      !values.targetLanguages.length ||
                      languagesRequiringEnglish.length > 0 ||
                      isWaitingForOriginalCourse
                    }
                  >
                    {t('course.translation.startTranslation')}
                  </AppButton>
                </FlexContainer>
              </FlexContainer>
            </Form>
          );
        }}
      </Formik>
    </FlexContainer>
  );
};
